import React, { Component } from "react";
import { observer } from "mobx-react";

import { IDeviceTeleopCustomStreamConfiguration } from "@formant/core/model/IDeviceTeleopCustomStreamConfiguration";
import { IDeviceTeleopRosStreamConfiguration } from "@formant/core/model/IDeviceTeleopRosStreamConfiguration";
import { ITeleopJoystickConfiguration } from "@formant/core/model/ITeleopJoystickConfiguration";
import { IValidation } from "@formant/core/model/IValidation";
import { rosTypeTwist } from "@formant/core/model/rosTypes";
import { rtcTwistStreamType } from "@formant/core/model/rtcStreamTypes";
import { Fader } from "@formant/ui/component/common/Fader";

import {
    defaultNewValueJoystickDebounce,
    defaultSameValueJoystickDebounce,
    lowNewValueJoystickDebounce,
    lowSameValueJoystickDebounce,
} from "../../utils/joystickDebounce";
import { Heading2 } from "../common/Heading2";
import { Help } from "../common/Help";
import { StandardButton } from "../common/StandardButton";
import { Switch } from "../common/Switch";
import { TeleopJoysticks } from "../Teleop/TeleopModule/TeleopJoysticks";
import {
    EditTeleopStreamPopup,
    IEditTeleopStreamPopupProps,
} from "./EditTeleopStreamPopup";
import { JoystickConfigurationRow } from "./JoystickConfigurationRow";
import { JoystickConfigurationRowFake } from "./JoystickConfigurationRowFake";
import * as styles from "./TeleopJoystickConfiguration.scss";

export interface ITeleopJoystickConfigurationProps extends IEditTeleopStreamPopupProps {
    joysticks: ITeleopJoystickConfiguration[];
    validation: IValidation;
    onSave: (joysticks: ITeleopJoystickConfiguration[]) => void;
    editingVelocityStreamWithName: string;
    customStreams: IDeviceTeleopCustomStreamConfiguration[];
    rosStreams: IDeviceTeleopRosStreamConfiguration[];
}

@observer
export class TeleopJoystickConfiguration extends Component<ITeleopJoystickConfigurationProps> {
    public render() {
        const {
            joysticks,
            validation,
            onClose,
            visible,
            editingVelocityStreamWithName,
        } = this.props;
        const { customStreams, rosStreams } = this.props;

        const possibleCustomStreamNames = customStreams
            .filter(
                _ =>
                    _.rtcStreamType === rtcTwistStreamType &&
                    _.mode === "command",
            )
            .map(_ => _.name);

        const possibleRosStreamNames = rosStreams
            .filter(_ => _.topicType === rosTypeTwist && _.mode === "command")
            .map(_ => _.topicName);

        const possibleStreamNames = [
            ...possibleCustomStreamNames,
            ...possibleRosStreamNames,
        ];

        const joystickRows = joysticks.map((_, i) => (
            <JoystickConfigurationRow
                key={i}
                index={i}
                joystick={_}
                validation={validation}
                onEdit={this.onEditJoystick}
                onRemove={this.onRemoveJoystick}
            />
        ));

        if (joysticks.length === 0 && possibleStreamNames.length > 1) {
            return (
                <EditTeleopStreamPopup
                    visible={visible}
                    onClose={onClose}
                    title="Joystick configuration"
                    className={styles["teleop-joystick-configuration"]}
                >
                    <div
                        className={
                            styles["add-teleop-joysticks-button-container"]
                        }
                    >
                        <p>
                            No joystick has been configured. Please add a
                            joystick to configure the joystick settings on this
                            stream.
                        </p>
                        <StandardButton
                            className={styles["add-teleop-joysticks-button"]}
                            onClick={() => {
                                this.onAddJoystick();
                                this.onSetVelocityStreamForJoysticks(
                                    editingVelocityStreamWithName,
                                );
                            }}
                        >
                            Add Teleop Joystick
                        </StandardButton>
                    </div>
                </EditTeleopStreamPopup>
            );
        }

        if (
            joysticks.length > 0 &&
            editingVelocityStreamWithName !==
                joysticks[0]?.velocityStreamName &&
            possibleStreamNames.length > 1
        ) {
            return (
                <EditTeleopStreamPopup
                    visible={visible}
                    onClose={onClose}
                    title="Joystick configuration"
                    className={styles["teleop-joystick-configuration"]}
                >
                    <p>
                        The joysticks are currently configured for a different
                        twist topic.
                    </p>
                    <Switch
                        state={
                            editingVelocityStreamWithName ===
                            joysticks[0]?.velocityStreamName
                        }
                        onChange={_ => {
                            this.onSetVelocityStreamForJoysticks(
                                editingVelocityStreamWithName,
                            );
                        }}
                        label="Set topic as Joystick Topic"
                    />
                </EditTeleopStreamPopup>
            );
        }

        return (
            <EditTeleopStreamPopup
                visible={visible}
                onClose={onClose}
                title="Joystick configuration"
                className={styles["teleop-joystick-configuration"]}
            >
                <Help
                    text="Click for docs."
                    to="https://docs.formant.io/docs/getting-started-configure-joystick-control"
                />
                {possibleStreamNames.length > 1 && (
                    <Switch
                        state={
                            editingVelocityStreamWithName ===
                            joysticks[0]?.velocityStreamName
                        }
                        label="Set topic as Joystick Topic"
                    />
                )}
                <Fader
                    className={styles.joysticks}
                    visible={joystickRows.length > 0}
                >
                    {joystickRows}
                </Fader>
                {joystickRows.length < 2 && (
                    <JoystickConfigurationRowFake
                        onAdd={this.onAddJoystick}
                        position={
                            joysticks.length === 1
                                ? (joysticks[0].position || "right") === "right"
                                    ? "left"
                                    : "right"
                                : "right"
                        }
                    />
                )}
                {joystickRows.length > 0 && (
                    <div className={styles.preview}>
                        <Heading2>Preview</Heading2>
                        <div className={styles["preview-content"]}>
                            <TeleopJoysticks
                                configuration={joysticks}
                                debug={true}
                            />
                        </div>
                    </div>
                )}
                {joystickRows.length > 0 && (
                    <Switch
                        state={
                            joysticks[0].disableTriggerJoystickMapping === true
                        }
                        label="Disable Gamepad Triggers"
                        onChange={this.onChangeDisableTriggerJoystickMapping}
                        info="If set to Yes, gamepad triggers will be disabled from joystick control and will not be mapped to a joystick value."
                        stateLabels={["Yes", "No"]}
                    />
                )}
                {joystickRows.length > 0 && (
                    <Switch
                        state={joysticks[0].disableGamepadAndKeyboard === true}
                        label="Disable Gamepad Input and Keyboard Input"
                        onChange={this.onChangeDisableGamepadAndKeyboard}
                        info="If set to Yes, all joystick input from gamepad(s) and keyboard will be disabled. Only on-screen joystick inputs will be sent to your device."
                        stateLabels={["Yes", "No"]}
                    />
                )}
                {joystickRows.length > 0 && (
                    <Switch
                        state={
                            (joysticks[0].sameValueDebounce ??
                                defaultSameValueJoystickDebounce) !==
                                defaultSameValueJoystickDebounce &&
                            (joysticks[0].newValueDebounce ??
                                defaultNewValueJoystickDebounce) !==
                                defaultNewValueJoystickDebounce
                        }
                        label="Increase Joystick Publish Rate"
                        onChange={this.onChangeReduceJoystickDebounce}
                        info={`Increase update rate of new joystick values (e.g., push forward) from every ${defaultNewValueJoystickDebounce} s to every ${lowNewValueJoystickDebounce} s, and repeating joystick values (e.g., hold forward) from every ${defaultSameValueJoystickDebounce} s to every ${lowSameValueJoystickDebounce} s.`}
                        stateLabels={["disabled", "enabled"]}
                    />
                )}
            </EditTeleopStreamPopup>
        );
    }

    private onChangeReduceJoystickDebounce = (newState: boolean) => {
        const { onSave, joysticks } = this.props;
        joysticks.forEach(joystick => {
            joystick.newValueDebounce = newState
                ? lowNewValueJoystickDebounce
                : defaultNewValueJoystickDebounce;
            joystick.sameValueDebounce = newState
                ? lowSameValueJoystickDebounce
                : defaultSameValueJoystickDebounce;
        });
        onSave(joysticks);
    };

    private onSetVelocityStreamForJoysticks = (streamName: string) => {
        const { onSave, joysticks } = this.props;
        joysticks.forEach(joystick => {
            joystick.velocityStreamName = streamName;
        });
        onSave(joysticks);
    };

    private onChangeDisableTriggerJoystickMapping = (newState: boolean) => {
        const { onSave, joysticks } = this.props;
        joysticks.forEach(
            joystick => (joystick.disableTriggerJoystickMapping = newState),
        );
        onSave(joysticks);
    };

    private onChangeDisableGamepadAndKeyboard = (newState: boolean) => {
        const { onSave, joysticks } = this.props;
        joysticks.forEach(
            joystick => (joystick.disableGamepadAndKeyboard = newState),
        );
        onSave(joysticks);
    };

    private onAddJoystick = () => {
        const { onSave, joysticks } = this.props;

        if (
            joysticks.length === 0 ||
            joysticks.some(_ => _.position === "left")
        ) {
            joysticks.push(this.getRightJoystickDefaults());
        } else if (joysticks.some(_ => _.position === "right")) {
            joysticks.push(this.getLeftJoystickDefaults());
        } else {
            return;
        }

        onSave(joysticks);
    };

    private onRemoveJoystick = (index: number) => {
        const { onSave, joysticks } = this.props;

        if (joysticks.length > 0) {
            joysticks.splice(index, 1);
        }

        onSave(joysticks);
    };

    private onEditJoystick = (
        joystick: ITeleopJoystickConfiguration,
        i: number,
    ) => {
        const { onSave, joysticks } = this.props;

        joysticks[i] = joystick;
        onSave(joysticks);
    };

    private getRightJoystickDefaults: () => ITeleopJoystickConfiguration =
        () => {
            const { joysticks } = this.props;
            const disableTriggerJoystickMapping =
                joysticks.length === 0
                    ? undefined
                    : joysticks[0].disableTriggerJoystickMapping;
            const disableGamepadAndKeyboard =
                joysticks.length === 0
                    ? undefined
                    : joysticks[0].disableGamepadAndKeyboard;
            const sameValueDebounce =
                joysticks.length === 0
                    ? undefined
                    : joysticks[0].sameValueDebounce;
            const newValueDebounce =
                joysticks.length === 0
                    ? undefined
                    : joysticks[0].newValueDebounce;

            return {
                position: "right",
                x: {
                    scale: 1,
                    expo: 2,
                    dimension: "angular-z",
                    gamepadAxis: 2,
                },
                y: {
                    scale: 1,
                    expo: 2,
                    dimension: "linear-x",
                    gamepadAxis: 3,
                },
                disableTriggerJoystickMapping,
                disableGamepadAndKeyboard,
                sameValueDebounce,
                newValueDebounce,
            };
        };

    private getLeftJoystickDefaults: () => ITeleopJoystickConfiguration =
        () => {
            const { joysticks } = this.props;
            const disableTriggerJoystickMapping =
                joysticks.length === 0
                    ? undefined
                    : joysticks[0].disableTriggerJoystickMapping;
            const disableGamepadAndKeyboard =
                joysticks.length === 0
                    ? undefined
                    : joysticks[0].disableGamepadAndKeyboard;
            return {
                position: "left",
                x: {
                    scale: 1,
                    expo: 2,
                    dimension: "linear-y",
                    gamepadAxis: 0,
                },
                y: {
                    scale: 1,
                    expo: 2,
                    dimension: "linear-z",
                    gamepadAxis: 1,
                },
                disableTriggerJoystickMapping,
                disableGamepadAndKeyboard,
            };
        };
}
